// Answering at the gate: the box's yes and no go through the command
// path (GateService.AnswerPending, the Connect twin of POST /pending),
// and the store moves optimistically — the waiting action is gone the
// moment the user answers, before the call resolves.
//
// The answer races the stream: the socket may deliver its own word
// about the gate (a newer action, or that action's settling) while the
// call is out, and a consumed delta is never sent again. The store's
// pending epoch tells the two apart: every stream write of the pending
// moves it, the optimistic hand does not — so when the response lands
// on a changed epoch, the stream spoke meanwhile and its word, being
// newer, stands; the answer then writes nothing. What waits after the
// answer (the response's Next) is only the server's word for the
// moment it read it, never a license to overwrite what came later.
import { Code, ConnectError } from "@connectrpc/connect";
import { PendingAnswer } from "@/gen/psypen/v1/gate_pb";
import type { PendingAction } from "@/gen/psypen/v1/session_pb";
import { pendingFromProto, type StoredPendingAction } from "@/session/types";
import type { SessionStore } from "@/session/store";

// The command path the box answers on: the Connect client satisfies
// this, and the tests bring their own.
export interface GateRpc {
  answer(id: string, answer: PendingAnswer): Promise<{ next?: PendingAction | undefined }>;
}

export type GateResult = { ok: true; reply: string } | { ok: false; error: string };

// The one answer is send or cancel (PendingAnswer in the contract);
// nothing that waits is rewritten by hand.
export async function answerAtGate(
  store: SessionStore,
  rpc: GateRpc,
  pending: StoredPendingAction,
  answer: PendingAnswer,
): Promise<GateResult> {
  // Optimistic first: the box is answered the moment it is clicked,
  // whatever the network does with the call.
  const seen = store.pendingEpoch();
  store.setPending(null);
  try {
    const res = await rpc.answer(pending.id, answer);
    // What waits after it — or nothing — but only when the stream said
    // nothing about the gate while the call was out.
    if (store.pendingEpoch() === seen) store.setPending(pendingFromProto(res.next));
    return { ok: true, reply: "" };
  } catch (err) {
    if (err instanceof ConnectError && err.code === Code.FailedPrecondition) {
      // Nothing is waiting any more: it was answered or it expired
      // elsewhere. The box stays gone unless the stream said otherwise
      // meanwhile (the epoch above).
      return { ok: true, reply: "" };
    }
    // The answer never landed: the action goes back to the box, so
    // nothing is lost by a dropped network — unless the stream brought
    // a newer word meanwhile; the box is then not ours to fill.
    if (store.pendingEpoch() === seen) store.setPending(pending);
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}
